import { baseApi } from 'segments/api/baseApi';
import { ToSnakeCase } from 'segments/types/transformTypes';
import { camelToSnakeCase } from 'segments/utils/helpers/transforms';

import { adapteUsersList } from '../utils/adapter';

import type {
  CreateUserParams,
  GetUsersListParams,
  GroupePaymentParams,
  UpdateUserDepositParams,
  UpdateUserParams,
  UpdateUserWithdrawParams,
  userGroupeOperationParams,
  UsersListTransformed,
} from './types';

export const usersApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getUsersList: builder.query<UsersListTransformed, void | GetUsersListParams>({
      query: (formData: GetUsersListParams) => ({
        url: '/admin/users/list',
        method: 'GET',
        params: camelToSnakeCase<GetUsersListParams, ToSnakeCase<GetUsersListParams>>(formData),
      }),
      transformResponse: adapteUsersList,
      providesTags: ['UsersList'],
      keepUnusedDataFor: 1,
    }),

    quickCreateUser: builder.mutation<{ success: boolean; data: { id: number } }, string>({
      query: (name) => ({ url: '/admin/users/quick-create', method: 'POST', body: { name } }),
      invalidatesTags: ['UsersList'],
    }),

    createUser: builder.mutation<unknown, CreateUserParams>({
      query: ({ birthDate, ...formData }) => ({
        url: '/admin/users/create',
        method: 'POST',
        body: { ...formData, 'birth_date': birthDate },
      }),
      invalidatesTags: ['UsersList'],
    }),

    updateUser: builder.mutation<unknown, UpdateUserParams>({
      query: ({ id, isBanned, ...formData }: UpdateUserParams) => ({
        url: `/admin/users/update/${id}`,
        method: 'PUT',
        body: { ...formData, 'is_banned': isBanned },
      }),
      invalidatesTags: ['UsersList'],
    }),

    updateUserDeposite: builder.mutation<unknown, UpdateUserDepositParams>({
      query: ({ id, amount, }: UpdateUserDepositParams) => ({
        url: `/admin/users/deposit/${id}`,
        method: 'PUT',
        params: { amount }
      }),
      invalidatesTags: ['UsersList'],
    }),

    updateUserWithdraw: builder.mutation<unknown, UpdateUserWithdrawParams>({
      query: ({ id, amount, }: UpdateUserWithdrawParams) => ({
        url: `/admin/users/withdraw/${id}`,
        method: 'PUT',
        params: { amount }
      }),
      invalidatesTags: ['UsersList'],
    }),
    addUserToGroupe: builder.mutation<unknown, userGroupeOperationParams>({
      query: ({ groupeId, userId }: userGroupeOperationParams) => ({
        url: `/admin/users/add_to_group/${userId}`,
        method: 'PUT',
        params: { 'group_id': groupeId }
      }),
      invalidatesTags: ['UsersList', 'GroupsList'],
    }),
    deleteUserFroomGroupe: builder.mutation<unknown, number>({
      query: (userId: number) => ({
        url: `/admin/users/delete_from_group/${userId}`,
        method: 'PUT',
      }),
      invalidatesTags: ['UsersList', 'GroupsList'],
    }),
    payUserGroupe: builder.mutation<unknown, GroupePaymentParams>({
      query: ({ userId, date }) => ({
        url: `/admin/users/debit_group_payment/${userId}`,
        method: 'PUT',
        params: {
          month: date.month() + 1,
          year: date.year()
        }
      }),
      invalidatesTags: ['UsersList', 'GroupsList'],
    }),
  }),
});

export const {
  useGetUsersListQuery,
  useUpdateUserMutation,
  useUpdateUserDepositeMutation,
  useUpdateUserWithdrawMutation,
  useAddUserToGroupeMutation,
  useDeleteUserFroomGroupeMutation,
  usePayUserGroupeMutation,
  useCreateUserMutation,
  useQuickCreateUserMutation
} = usersApi;
